import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { enhancedApi } from '../api';

function getScoreColor(score) {
  if (score == null) return 'grey.500';
  const s = Number(score);
  if (s >= 70) return 'success.main';
  if (s >= 40) return 'warning.main';
  return 'error.main';
}

function getRiskLabel(riskLevel) {
  if (!riskLevel) return '—';
  const r = String(riskLevel).toLowerCase();
  if (r === 'healthy') return 'Healthy';
  if (r === 'at_risk') return 'At Risk';
  if (r === 'critical') return 'Critical';
  return riskLevel;
}

function getRiskChipColor(riskLevel) {
  if (!riskLevel) return 'default';
  const r = String(riskLevel).toLowerCase();
  if (r === 'healthy') return 'success';
  if (r === 'at_risk') return 'warning';
  if (r === 'critical') return 'error';
  return 'default';
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

const HealthScoreTab = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    enhancedApi
      .getClientHealthScore(clientId)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load health score');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  const score = data?.score ?? 0;
  const riskLevel = data?.risk_level ?? '';
  const factors = Array.isArray(data?.factors) ? data.factors : [];
  const lastCalculated = data?.last_calculated ?? data?.last_calculated_at;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start', mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 160,
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              border: 4,
              borderColor: getScoreColor(score),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <Typography variant="h3" fontWeight={700} sx={{ color: getScoreColor(score) }}>
              {score}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Health Score
          </Typography>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Risk Level
          </Typography>
          <Chip
            label={getRiskLabel(riskLevel)}
            color={getRiskChipColor(riskLevel)}
            size="medium"
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            Last calculated: {formatTimestamp(lastCalculated)}
          </Typography>
        </Box>
      </Box>

      {factors.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Factors Breakdown
          </Typography>
          <List dense disablePadding>
            {factors.map((f, idx) => (
              <ListItem key={f.name ?? idx} disablePadding sx={{ py: 0.5 }}>
                <ListItemText
                  primary={f.name ?? f.factor_name ?? '—'}
                  secondary={f.contribution != null ? `Contribution: ${f.contribution}` : null}
                />
                {f.contribution != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {f.contribution}
                  </Typography>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default HealthScoreTab;
